import { Trans } from "@lingui/macro";
import { UiDialogResponsive } from "@smp/ui";
import clsx from "clsx";
import { FC, useCallback, useMemo, useRef, useState } from "react";
import { useUpdateEffect } from "react-use";

import { PaymentV2 } from "@/screens/app/components/payment_v2";
import { gscSendUserParams } from "@/utils";

import { IInvoiceFormProps, InvoiceForm } from "./components/invoice_form";
import classes from "./dialog_purchase_v2.module.css";
import { IDialogPurchaseV2Props } from "./dialog_purchase_v2.types";

export const DialogPurchaseV2: FC<IDialogPurchaseV2Props> = ({
  open,
  onClose,
  onSuccess,
  onChangeProduct,
  product,
  productName,
}) => {
  const isPaymentDeclined = useRef(false);
  const [invoiceResponse, setInvoiceResponse] =
    useState<Parameters<IInvoiceFormProps["onCreateInvoice"]>[0]>();

  const isContentHidden = useMemo(
    () =>
      !!invoiceResponse &&
      ["cloudpayments"].includes(invoiceResponse.paymentProvider.id),
    [invoiceResponse],
  );

  const handleDecline = useCallback(() => {
    isPaymentDeclined.current = true;
    gscSendUserParams({ is_payment_canceled: true });
    setInvoiceResponse(undefined);
  }, []);

  const handleClose = useCallback(() => {
    setInvoiceResponse(undefined);

    if (!isPaymentDeclined.current) {
      isPaymentDeclined.current = true;
      gscSendUserParams({ is_payment_canceled: true });
    }

    if (invoiceResponse?.invoice.status === "complete") {
      onSuccess?.();
    }

    onClose();
  }, [invoiceResponse?.invoice.status, onClose, onSuccess]);

  const handleSuccess = useCallback(() => {
    setInvoiceResponse(undefined);

    onSuccess?.();
    onClose();
  }, [onClose, onSuccess]);

  const handleCreateInvoice = useCallback<
    NonNullable<IInvoiceFormProps["onCreateInvoice"]>
  >(
    (resp) => {
      if (resp.invoice.status === "complete") {
        onSuccess?.();
        return;
      }

      setInvoiceResponse(resp);
    },
    [onSuccess],
  );

  useUpdateEffect(() => {
    if (
      invoiceResponse?.invoice.bill?.id ||
      invoiceResponse?.invoice.recurring_payment?.id
    ) {
      isPaymentDeclined.current = false;
    }
  }, [
    invoiceResponse?.invoice.bill?.id,
    invoiceResponse?.invoice.recurring_payment?.id,
  ]);

  return (
    <UiDialogResponsive
      fullWidth
      maxWidth="md"
      onClose={handleClose}
      title={<Trans>Payment</Trans>}
      open={open}
      className={clsx(classes.dialog, isContentHidden && classes.dialog_hidden)}
    >
      {!invoiceResponse ? (
        <InvoiceForm
          initialProduct={product}
          onCreateInvoice={handleCreateInvoice}
          onChangeProduct={onChangeProduct}
          productName={productName}
        />
      ) : (
        <PaymentV2
          onDecline={handleDecline}
          onSuccess={handleSuccess}
          invoice={invoiceResponse.invoice}
          provider={invoiceResponse.paymentProvider}
        />
      )}
    </UiDialogResponsive>
  );
};
